import { Navigate } from 'react-router-dom'
import {
  IconShirt, IconBox, IconBackpack, IconLock, IconShieldLock,
  IconSparkles, IconShare, IconTrash, IconAdjustments, IconArrowRight,
  IconCheck, IconBuildingStore, IconChevronRight,
} from '@tabler/icons-react'
import { useAuth } from '../../context/AuthContext.tsx'

// ── Small building blocks ─────────────────────────────────────────────────────

type Feature = {
  icon: React.ReactNode
  title: string
  body: string
}

const FEATURES: Feature[] = [
  {
    icon: <IconBox size={20} />,
    title: 'Catalog every piece',
    body: 'Record brand, size, condition, and colors. Separate what a piece was made for from how you actually use it, then add your own custom tags.',
  },
  {
    icon: <IconLock size={20} />,
    title: 'Lockers and Gear Bags',
    body: 'Mirror your real storage. Nest gear bags inside lockers so the app matches the shelf, the trunk, or the closet where things actually live.',
  },
  {
    icon: <IconBackpack size={20} />,
    title: 'Kit Collections',
    body: 'Group items into ready sets. Build a collection for a scene, an event, or a weekend away, without moving anything around.',
  },
  {
    icon: <IconSparkles size={20} />,
    title: 'Identify with AI',
    body: 'Snap a photo and let AI fill in the title, brand, condition, and tags. Every suggestion is yours to accept or change.',
  },
  {
    icon: <IconShare size={20} />,
    title: 'Share on your terms',
    body: 'Keep a collection private, share it with the community, or make it public. Your private notes never leave your account.',
  },
  {
    icon: <IconTrash size={20} />,
    title: 'Never lose a thing',
    body: 'Deleted something by mistake? Recently Deleted keeps it recoverable for 30 days before anything is permanently removed.',
  },
]

type Step = { n: string; title: string; body: string }

const STEPS: Step[] = [
  {
    n: '01',
    title: 'Add your gear',
    body: 'Create items by hand, or start from a photo and let AI do the first pass for you.',
  },
  {
    n: '02',
    title: 'Organize it your way',
    body: 'Sort gear into lockers and gear bags, then assemble kit collections for whatever is coming up.',
  },
  {
    n: '03',
    title: 'Share what you choose',
    body: 'Decide what stays private and what you show the community. You are always in control.',
  },
]

const PRIVACY_POINTS: string[] = [
  'Every account is fully isolated. Your gear is yours alone.',
  'A public description and always-private notes live on every item, so you decide what is shared.',
  'Set visibility per collection: private, community, or public.',
  'Nothing is hard deleted right away. Removed items sit in Recently Deleted first.',
]

// ── Wordmark ──────────────────────────────────────────────────────────────────

function Wordmark({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  const text = size === 'lg' ? 'text-lg' : 'text-sm'
  const icon = size === 'lg' ? 22 : 18
  return (
    <div className="flex items-center gap-2">
      <IconShirt size={icon} className="text-brand-orange" />
      <span className={`${text} font-semibold tracking-tight text-zinc-100`}>
        Gear<span className="text-brand-orange">Locker</span>
      </span>
    </div>
  )
}

// ── Faithful in-theme preview of the real Locker Room screen ──────────────────

function AppPreview() {
  const stats = [
    { label: 'Lockers', value: 3, icon: <IconLock size={18} /> },
    { label: 'Kit Collections', value: 7, icon: <IconBackpack size={18} /> },
    { label: 'Gear Items', value: 42, icon: <IconBox size={18} /> },
  ]
  const recent = [
    { title: 'Leather Harness', meta: 'Mr. S Leather · L', tag: 'Excellent' },
    { title: 'Neoprene Hood', meta: 'Regulation · One size', tag: 'Good' },
    { title: 'Hockey Pads Set', meta: 'CCM · Senior', tag: 'Like new' },
  ]
  return (
    <div
      aria-hidden="true"
      className="select-none pointer-events-none rounded-2xl border border-border-default bg-surface-overlay overflow-hidden shadow-2xl shadow-black/40"
    >
      {/* Fake top bar, matching TopNav */}
      <div className="flex items-center h-11 px-4 bg-surface-overlay border-b border-border-default">
        <Wordmark />
        <div className="hidden sm:flex items-center gap-1 ml-6 text-[11px]">
          <span className="px-2.5 py-1 rounded-md text-brand-orange bg-surface-raised">My Locker Room</span>
          <span className="px-2.5 py-1 rounded-md text-zinc-500">Kit Collections</span>
          <span className="px-2.5 py-1 rounded-md text-zinc-500">Community</span>
        </div>
        <div className="ml-auto w-6 h-6 rounded-full bg-amber-950 border border-brand-orange flex items-center justify-center text-brand-orange text-[10px] font-semibold">
          SW
        </div>
      </div>

      {/* Body */}
      <div className="p-5 bg-surface-base">
        <div className="mb-4">
          <div className="text-base font-semibold text-zinc-100">Locker Room</div>
          <div className="text-xs text-zinc-500 mt-0.5">Your collection at a glance.</div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          {stats.map((s) => (
            <div
              key={s.label}
              className="flex items-center gap-3 p-3 rounded-xl bg-surface-overlay border border-border-default"
            >
              <div className="w-9 h-9 rounded-lg bg-surface-raised border border-border-default flex items-center justify-center text-brand-orange shrink-0">
                {s.icon}
              </div>
              <div className="min-w-0">
                <div className="text-xl font-semibold text-zinc-100 leading-none">{s.value}</div>
                <div className="text-[11px] text-zinc-500 mt-1 truncate">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500 mb-2">Recent gear</div>
        <div className="rounded-xl border border-border-default bg-surface-overlay overflow-hidden">
          {recent.map((r, i) => (
            <div
              key={r.title}
              className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-border-subtle' : ''}`}
            >
              <div className="w-7 h-7 rounded-lg bg-surface-raised border border-border-default flex items-center justify-center shrink-0">
                <IconBox size={13} className="text-zinc-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-zinc-200 truncate">{r.title}</div>
                <div className="text-[11px] text-zinc-500 truncate">{r.meta}</div>
              </div>
              <span className="text-[10px] text-zinc-500 border border-border-default rounded-full px-2 py-0.5 shrink-0">
                {r.tag}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function LandingPage() {
  const { isAuthenticated, login } = useAuth()

  // Anyone already signed in skips the marketing page and goes straight in.
  if (isAuthenticated) {
    return <Navigate to="/locker-room" replace />
  }

  // Preserve the existing sign-in error surface: WorkOS failures and logout
  // both land here with an optional ?error param.
  const error = new URLSearchParams(window.location.search).get('error')

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const primaryBtn =
    'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-brand-orange text-white text-sm font-medium hover:bg-brand-orange-dark transition-colors'
  const ghostBtn =
    'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-border-default text-zinc-200 text-sm font-medium hover:border-border-strong hover:bg-surface-raised transition-colors'

  return (
    <div className="min-h-screen bg-surface-base text-zinc-100">
      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-border-default bg-surface-base/80 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-14 flex items-center">
          <Wordmark size="sm" />
          <nav className="ml-auto flex items-center gap-2">
            <button onClick={() => scrollTo('features')} className="hidden sm:inline text-xs text-zinc-500 hover:text-zinc-200 transition-colors px-3 py-2">
              Features
            </button>
            <button onClick={() => scrollTo('how')} className="hidden sm:inline text-xs text-zinc-500 hover:text-zinc-200 transition-colors px-3 py-2">
              How it works
            </button>
            <button
              onClick={login}
              className="px-4 py-2 rounded-lg bg-brand-orange text-white text-xs font-medium hover:bg-brand-orange-dark transition-colors"
            >
              Sign in
            </button>
          </nav>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Soft brand glow, kept subtle to match the dark surfaces */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(60% 55% at 50% -5%, rgba(224,122,32,0.16), transparent 70%)',
          }}
        />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-16 pb-12 sm:pt-24 sm:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
            {/* Copy */}
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-border-default bg-surface-overlay px-3 py-1 text-xs text-zinc-400">
                <IconShieldLock size={14} className="text-brand-orange" />
                Private by design
              </div>

              <h1 className="mt-5 text-3xl sm:text-5xl font-semibold tracking-tight text-zinc-100 leading-[1.1]">
                Every piece of gear, in your own private locker room.
              </h1>

              <p className="mt-5 text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed">
                GearLocker is inventory management built for the leather, rubber, sports, pup,
                and fetish gear community. Catalog what you own, organize it the way you store it,
                and share only what you choose.
              </p>

              {error && (
                <p className="mt-5 text-sm text-brand-orange">
                  {error === 'auth_failed'
                    ? 'Sign in failed. Please try again.'
                    : 'Something went wrong. Please try again.'}
                </p>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={login} className={primaryBtn}>
                  Get started free <IconArrowRight size={16} />
                </button>
                <button onClick={() => scrollTo('how')} className={ghostBtn}>
                  See how it works
                </button>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1.5"><IconCheck size={14} className="text-brand-orange" /> Per-user isolation</span>
                <span className="inline-flex items-center gap-1.5"><IconCheck size={14} className="text-brand-orange" /> Notes stay private</span>
                <span className="inline-flex items-center gap-1.5"><IconCheck size={14} className="text-brand-orange" /> Discreet by default</span>
              </div>
            </div>

            {/* Preview */}
            <div className="lg:pl-4">
              <AppPreview />
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────────── */}
      <section id="features" className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20 scroll-mt-20">
        <div className="max-w-2xl">
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-400">What you can do</div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-100">
            Everything your collection needs, nothing it does not.
          </h2>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group rounded-xl border border-border-default bg-surface-overlay p-6 hover:border-border-strong hover:bg-surface-raised transition-all"
            >
              <div className="w-11 h-11 rounded-lg bg-surface-raised border border-border-default flex items-center justify-center text-zinc-400 group-hover:text-brand-orange group-hover:border-brand-orange/30 transition-colors">
                {f.icon}
              </div>
              <h3 className="mt-4 text-base font-semibold text-zinc-100">{f.title}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>

        {/* Customization callout, its own full-width card */}
        <div className="mt-4 rounded-xl border border-border-default bg-surface-overlay p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-11 h-11 rounded-lg bg-surface-raised border border-border-default flex items-center justify-center text-brand-orange shrink-0">
            <IconAdjustments size={20} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-zinc-100">Make it yours</h3>
            <p className="mt-1 text-sm text-zinc-400 leading-relaxed max-w-3xl">
              Rename fields and add your own values. GearLocker adapts to your vocabulary
              instead of forcing you into ours, so the app reads the way you already think
              about your gear.
            </p>
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────────── */}
      <section id="how" className="border-y border-border-default bg-surface-overlay/40 scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-2xl">
            <div className="text-xs font-medium uppercase tracking-widest text-zinc-400">How it works</div>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-100">
              Up and running in three steps.
            </h2>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-xl border border-border-default bg-surface-overlay p-6">
                <div className="text-2xl font-semibold text-brand-orange/80 tabular-nums">{s.n}</div>
                <h3 className="mt-3 text-base font-semibold text-zinc-100">{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Privacy ─────────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border-default bg-surface-overlay px-3 py-1 text-xs text-zinc-400">
              <IconShieldLock size={14} className="text-brand-orange" />
              Built for discretion
            </div>
            <h2 className="mt-5 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-100">
              Your collection stays yours.
            </h2>
            <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-xl">
              This is a private space to keep track of gear that matters to you. Sharing is
              always something you turn on, never a default you have to turn off.
            </p>
          </div>

          <ul className="space-y-3">
            {PRIVACY_POINTS.map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 rounded-xl border border-border-default bg-surface-overlay p-4"
              >
                <IconCheck size={18} className="text-brand-orange mt-0.5 shrink-0" />
                <span className="text-sm text-zinc-300 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Coming soon ─────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="rounded-xl border border-border-default bg-surface-overlay p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-11 h-11 rounded-lg bg-surface-raised border border-border-default flex items-center justify-center text-brand-orange shrink-0">
            <IconBuildingStore size={20} />
          </div>
          <div className="flex-1">
            <div className="text-xs font-medium uppercase tracking-widest text-zinc-500">Coming soon</div>
            <h3 className="mt-1 text-base font-semibold text-zinc-100">A marketplace for gear</h3>
            <p className="mt-1 text-sm text-zinc-400 leading-relaxed max-w-3xl">
              Trade and sell with other members, with offers and counter-offers, all built on
              the same private foundation you already trust.
            </p>
          </div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────────────── */}
      <section className="border-t border-border-default">
        <div className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(50% 120% at 50% 100%, rgba(224,122,32,0.14), transparent 70%)',
            }}
          />
          <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24 text-center">
            <h2 className="text-2xl sm:text-4xl font-semibold tracking-tight text-zinc-100">
              Ready to get your collection in order?
            </h2>
            <p className="mt-4 text-sm sm:text-base text-zinc-400 max-w-lg mx-auto">
              Create your locker and add your first piece in minutes.
            </p>
            <div className="mt-8 flex justify-center">
              <button onClick={login} className={primaryBtn}>
                Get started free <IconChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer className="border-t border-border-default">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <div>
            <Wordmark size="sm" />
            <p className="mt-2 text-xs text-zinc-500">Inventory management for your gear collection.</p>
          </div>
          <div className="sm:ml-auto flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500">
            <button onClick={() => scrollTo('features')} className="hover:text-zinc-300 transition-colors">Features</button>
            <button onClick={() => scrollTo('how')} className="hover:text-zinc-300 transition-colors">How it works</button>
            <button onClick={login} className="hover:text-zinc-300 transition-colors">Sign in</button>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 pb-10 flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-zinc-600">
          <span>© {new Date().getFullYear()} GearLocker</span>
          <span className="sm:ml-auto">For adults, 18 and over.</span>
        </div>
      </footer>
    </div>
  )
}
